import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { db } from '@/lib/firebase';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, getDocs } from 'firebase/firestore';
import { capitalizeWords } from '@/lib/utils';
import { buildKaraokePublicUrl } from '@/lib/urlHelpers';

import { Mic, Music, Heart, Users, Clock, QrCode, ExternalLink, Radio } from 'lucide-react';
import QRCode from 'qrcode.react';

const KaraokeTVDisplayPage = () => {
  const { urlSlug } = useParams();
  const [profile, setProfile] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upNextAnnouncement, setUpNextAnnouncement] = useState(null);
  const [announcementColorScheme, setAnnouncementColorScheme] = useState(0);
  const [queueScrollIndex, setQueueScrollIndex] = useState(0);
  const [queueScrollDirection, setQueueScrollDirection] = useState('static');
  const [tickerSettings, setTickerSettings] = useState({ enabled: false, text: '' });
  // The slug fallback listener is created inside a snapshot callback, so its
  // unsubscribe has to be held here for the effect cleanup to reach it.
  const fallbackProfileUnsubRef = useRef(null);
  const publicUrl = useMemo(
    () => buildKaraokePublicUrl(profile?.url_slug || urlSlug),
    [profile?.url_slug, urlSlug]
  );
  
  // Combined color schemes for exact order you specified
  const combinedColorSchemes = [
    {
      name: 'Golden + Blue',
      songTitleGradient: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706, #92400e)',
      requesterTextColor: 'text-blue-300',
      requesterBoxFrom: 'from-purple-600',
      requesterBoxTo: 'to-indigo-600'
    },
    {
      name: 'Cyan + Orange',
      songTitleGradient: 'linear-gradient(135deg, #06b6d4, #0891b2, #0e7490, #155e75)',
      requesterTextColor: 'text-orange-300',
      requesterBoxFrom: 'from-cyan-600',
      requesterBoxTo: 'to-blue-600'
    },
    {
      name: 'Green + Purple',
      songTitleGradient: 'linear-gradient(135deg, #10b981, #059669, #047857, #065f46)',
      requesterTextColor: 'text-purple-300',
      requesterBoxFrom: 'from-emerald-600',
      requesterBoxTo: 'to-teal-600'
    },
    {
      name: 'Golden + Pink',
      songTitleGradient: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706, #92400e)',
      requesterTextColor: 'text-pink-300',
      requesterBoxFrom: 'from-purple-600',
      requesterBoxTo: 'to-indigo-600'
    },
    {
      name: 'Cyan + Indigo',
      songTitleGradient: 'linear-gradient(135deg, #06b6d4, #0891b2, #0e7490, #155e75)',
      requesterTextColor: 'text-indigo-300',
      requesterBoxFrom: 'from-cyan-600',
      requesterBoxTo: 'to-blue-600'
    },
    {
      name: 'Green + Teal',
      songTitleGradient: 'linear-gradient(135deg, #10b981, #059669, #047857, #065f46)',
      requesterTextColor: 'text-teal-300',
      requesterBoxFrom: 'from-emerald-600',
      requesterBoxTo: 'to-teal-600'
    }
  ];
  
  // Function to rotate color scheme
  const rotateColorScheme = () => {
    // Use the single combined scheme list; avoids undefined reference
    setAnnouncementColorScheme((prev) => (prev + 1) % combinedColorSchemes.length);
  };
  
  // Function to advance to next color scheme for new announcements
  const advanceColorScheme = () => {
    setAnnouncementColorScheme((prev) => (prev + 1) % combinedColorSchemes.length);
  };

  // Load ticker settings
  const loadTickerSettings = () => {
    if (!profile?.id) return () => {};
    
    try {
      console.log('📺 TV Display: Loading ticker settings for profile:', profile.id);
      const tickerRef = doc(db, 'karaoke_ticker_settings', profile.id);
      
      const unsubscribe = onSnapshot(tickerRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          console.log('📺 TV Display: Ticker settings loaded:', data);
          const newSettings = {
            enabled: data.enabled || false,
            text: data.text || ''
          };
          console.log('📺 TV Display: Setting ticker settings to:', newSettings);
          setTickerSettings(newSettings);
        } else {
          console.log('📺 TV Display: No ticker settings found');
          setTickerSettings({ enabled: false, text: '' });
        }
      }, (error) => {
        console.error('❌ TV Display: Error loading ticker settings:', error);
      });

      return unsubscribe;
    } catch (err) {
      console.error('❌ TV Display: Exception loading ticker settings:', err);
      return () => {};
    }
  };
  

  
  // TV Display Settings from Control Panel
  const [settings, setSettings] = useState({
    preset: 'default',
    primaryColor: '#7c3aed',
    secondaryColor: '#4f46e5',
    accentColor: '#ec4899',
    floatingNotes: true,
    waveLines: true,
    particles: true,
    gridPattern: true,
    colorShifts: true,
    animationSpeed: 3,
    particleDensity: 3,
    noteOpacity: 0.2,
    showQRCode: true,
    showSocialLinks: true,
    showVenueInfo: true,
    showRequestQueue: true,
    fontSize: 'large',
    cardTransparency: 0.2,
    backdropBlur: true,
    liveIndicatorStyle: 'pulsing',
    queueAnimationStyle: 'slide',
    autoRefresh: true,
    refreshInterval: 30
  });

  // Fullscreen helpers to avoid calling undefined browser APIs
  const [isFullscreen, setIsFullscreen] = useState(false);

  const getFullscreenElement = () =>
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement;

  const requestFullscreenSafe = async (element) => {
    if (!element || typeof element !== 'object') {
      console.warn('Invalid element for fullscreen request.');
      return;
    }
    const request =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;
    if (!request || typeof request !== 'function') {
      console.warn('Fullscreen API is not available in this browser.');
      return;
    }
    try {
      await request.call(element);
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
    }
  };

  const exitFullscreenSafe = async () => {
    if (!document || typeof document !== 'object') {
      console.warn('Document object not available.');
      return;
    }
    const exit =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;
    if (!exit || typeof exit !== 'function') {
      console.warn('Fullscreen exit API is not available in this browser.');
      return;
    }
    try {
      await exit.call(document);
    } catch (err) {
      console.error('Failed to exit fullscreen:', err);
    }
  };

  const handleFullscreenToggle = async () => {
    try {
      if (getFullscreenElement()) {
        await exitFullscreenSafe();
      } else {
        await requestFullscreenSafe(document.documentElement);
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(!!getFullscreenElement());
    };
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    document.addEventListener('mozfullscreenchange', syncFullscreenState);
    document.addEventListener('MSFullscreenChange', syncFullscreenState);
    syncFullscreenState();
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
      document.removeEventListener('mozfullscreenchange', syncFullscreenState);
      document.removeEventListener('MSFullscreenChange', syncFullscreenState);
    };
  }, []);

  // Listen for messages from control panel
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'REFRESH_TV_DISPLAY') {
        console.log('🔄 Received refresh request from control panel');
        // Force reload settings from localStorage
        const savedSettings = localStorage.getItem('tv-display-settings');
        if (savedSettings) {
          try {
            const parsedSettings = JSON.parse(savedSettings);
            setSettings(prev => ({ ...prev, ...parsedSettings }));
            console.log('✅ Settings refreshed from control panel');
          } catch (error) {
            console.error('❌ Failed to parse settings from control panel:', error);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Load settings from localStorage
  useEffect(() => {
    const loadSettings = () => {
      const savedSettings = localStorage.getItem('tv-display-settings');
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          setSettings(prev => ({ ...prev, ...parsedSettings }));
        } catch (error) {
          console.log('Failed to parse TV display settings');
        }
      }
    };

    loadSettings();
    
    // Listen for settings updates from Control Panel
    const handleStorageChange = (e) => {
      if (e.key === 'tv-display-settings') {
        loadSettings();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check for updates every few seconds
    const settingsInterval = setInterval(loadSettings, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(settingsInterval);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = fetchProfile();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [urlSlug]);

  useEffect(() => {
    if (profile) {
      console.log('🔧 TV Display: Setting up subscriptions for profile:', profile.id);
      
      // Set up real-time listeners for live status and requests
      const liveStatusUnsubscribe = fetchLiveStatus();
      const requestsUnsubscribe = fetchRequests();
      const tickerUnsubscribe = loadTickerSettings();

      // Auto-refresh for TV displays (every 30 seconds)
      const autoRefreshInterval = setInterval(() => {
        console.log('🔄 TV Display: Auto-refreshing data...');
        // The onSnapshot listeners will automatically update the data
      }, 30000);

      return () => {
        console.log('🧹 TV Display: Cleaning up subscriptions');
        if (liveStatusUnsubscribe) liveStatusUnsubscribe();
        if (requestsUnsubscribe) requestsUnsubscribe();
        if (tickerUnsubscribe) tickerUnsubscribe();
        clearInterval(autoRefreshInterval);
      };
    }
  }, [profile]);

  // Auto-scrolling queue logic: Top 3 static for 3s, scroll down, scroll up, repeat
  useEffect(() => {
    if (!isLive || requests.length === 0) {
      setQueueScrollIndex(0);
      setQueueScrollDirection('static');
      return;
    }

    // Only show scrolling if there are more than 3 requests
    if (requests.length <= 3) {
      setQueueScrollIndex(0);
      setQueueScrollDirection('static');
      return;
    }

    let timeoutId;
    const SCROLL_DELAY = 700; // 700ms per song transition
    const STATIC_DELAY = 3000; // 3 seconds at top/bottom

    const scheduleNext = () => {
      if (queueScrollDirection === 'static' && queueScrollIndex === 0) {
        // At top, static for 3 seconds, then start scrolling down
        timeoutId = setTimeout(() => {
          setQueueScrollDirection('down');
          setQueueScrollIndex(3); // Start showing songs starting from index 3 (4th song)
        }, STATIC_DELAY);
      } else if (queueScrollDirection === 'down') {
        // Scrolling down - show next 3 songs
        const maxStartIndex = requests.length - 3; // Last position where we can show 3 songs
        if (queueScrollIndex < maxStartIndex) {
          timeoutId = setTimeout(() => {
            setQueueScrollIndex(prev => prev + 1);
          }, SCROLL_DELAY);
        } else {
          // Reached bottom (last 3 songs), stay for 3 seconds, then scroll up
          timeoutId = setTimeout(() => {
            setQueueScrollDirection('up');
            setQueueScrollIndex(maxStartIndex - 1); // Start from one before last position
          }, STATIC_DELAY);
        }
      } else if (queueScrollDirection === 'up') {
        // Scrolling up
        if (queueScrollIndex > 3) {
          timeoutId = setTimeout(() => {
            setQueueScrollIndex(prev => prev - 1);
          }, SCROLL_DELAY);
        } else if (queueScrollIndex === 3) {
          // About to reach top 3, go to static state
          timeoutId = setTimeout(() => {
            setQueueScrollDirection('static');
            setQueueScrollIndex(0);
          }, SCROLL_DELAY);
        } else {
          // Shouldn't happen, but reset to static if it does
          setQueueScrollDirection('static');
          setQueueScrollIndex(0);
        }
      }
    };

    scheduleNext();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLive, requests.length, queueScrollIndex, queueScrollDirection]);

  const fetchProfile = () => {
    try {
      console.log('🔍 TV Display: Fetching profile for slug:', urlSlug);
      
      // Normalize the URL slug: remove trailing slash, convert to lowercase, trim whitespace
      const normalizedSlug = urlSlug?.replace(/\/$/, '').toLowerCase().trim();
      console.log('🔍 TV Display: Normalized slug:', normalizedSlug, 'from:', urlSlug);
      
      // Query profiles collection for the URL slug (case-insensitive)
      const profilesRef = collection(db, 'profiles');
      
      // First try normalized lowercase (most common case)
      // Note: We don't filter by account_type here to support legacy accounts
      // The TV display will work for any profile with the matching slug
      let q = query(profilesRef, where('url_slug', '==', normalizedSlug));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        if (!querySnapshot.empty) {
          const profileData = querySnapshot.docs[0].data();
          const profileId = querySnapshot.docs[0].id;
          console.log('✅ TV Display: Profile fetched successfully:', profileData);
          console.log('🆔 TV Display: Profile ID:', profileId);
          console.log('🔗 TV Display: Profile url_slug:', profileData.url_slug);
          setProfile({ ...profileData, id: profileId });
          setLoading(false);
        } else {
          // Try original case as fallback
          const originalSlug = urlSlug?.replace(/\/$/, '').trim();
          console.log('🔍 TV Display: Trying original case slug:', originalSlug);
          const originalQ = query(profilesRef, where('url_slug', '==', originalSlug));
          // Replace any previous fallback listener; the outer snapshot can fire
          // repeatedly and would otherwise stack up orphaned listeners.
          if (fallbackProfileUnsubRef.current) {
            fallbackProfileUnsubRef.current();
            fallbackProfileUnsubRef.current = null;
          }
          const originalUnsubscribe = onSnapshot(originalQ, (originalSnapshot) => {
            if (!originalSnapshot.empty) {
              const profileData = originalSnapshot.docs[0].data();
              const profileId = originalSnapshot.docs[0].id;
              console.log('✅ TV Display: Profile fetched successfully with original case:', profileData);
              setProfile({ ...profileData, id: profileId });
              setLoading(false);
            } else {
              // Last resort: case-insensitive search
              console.log('🔍 TV Display: Trying case-insensitive search');
              getDocs(profilesRef).then((allProfilesSnapshot) => {
                const matchingProfile = allProfilesSnapshot.docs.find(doc => {
                  const slug = doc.data().url_slug;
                  return slug && slug.toLowerCase() === normalizedSlug;
                });
                if (matchingProfile) {
                  const profileData = matchingProfile.data();
                  const profileId = matchingProfile.id;
                  console.log('✅ TV Display: Profile found via case-insensitive search:', profileData);
                  setProfile({ ...profileData, id: profileId });
                  setLoading(false);
                } else {
                  console.error('❌ TV Display: Profile not found for slug:', urlSlug, 'normalized:', normalizedSlug);
                  setError('Profile not found');
                  setLoading(false);
                }
              }).catch((err) => {
                console.error('❌ TV Display: Error in case-insensitive search:', err);
                setError('Failed to load profile');
                setLoading(false);
              });
            }
          }, (error) => {
            console.error('❌ TV Display: Failed to load profile with original case:', error);
            setError('Failed to load profile');
            setLoading(false);
          });

          fallbackProfileUnsubRef.current = originalUnsubscribe;
        }
      }, (error) => {
        console.error('❌ TV Display: Failed to load profile:', error);
        setError('Failed to load profile');
        setLoading(false);
      });

      return () => {
        unsubscribe();
        if (fallbackProfileUnsubRef.current) {
          fallbackProfileUnsubRef.current();
          fallbackProfileUnsubRef.current = null;
        }
      };
    } catch (err) {
      console.error('❌ TV Display: Failed to load profile:', err);
      setError('Failed to load profile');
      setLoading(false);
      return () => {}; // Return empty function to prevent unsubscribe error
    }
  };

  const fetchLiveStatus = () => {
    if (!profile) return () => {};
    
    try {
      console.log('🔍 TV Display: Fetching live status for profile:', profile.id);
      const liveStatusRef = doc(db, 'live_status', profile.id);
      
      const unsubscribe = onSnapshot(liveStatusRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          console.log('✅ TV Display: Live status fetched:', data);
          setIsLive(data.is_live || false);
          
          // Handle up next announcement
          if (data.up_next_announcement) {
            console.log('📢 TV Display: Up next announcement received:', data.up_next_announcement);
            // Check if this is a new announcement (different from current one)
            if (!upNextAnnouncement ||
                upNextAnnouncement.song_title !== data.up_next_announcement.song_title ||
                upNextAnnouncement.artist_name !== data.up_next_announcement.artist_name ||
                upNextAnnouncement.requester_name !== data.up_next_announcement.requester_name) {
              // New announcement - advance color scheme
              advanceColorScheme();
              console.log('🎨 TV Display: Advancing to next color scheme');
            }
            setUpNextAnnouncement(data.up_next_announcement);
          } else {
            console.log('📢 TV Display: No up next announcement');
            setUpNextAnnouncement(null);
          }
        } else {
          console.log('📝 TV Display: No live status document found, setting to offline');
          setIsLive(false);
          setUpNextAnnouncement(null);
        }
      }, (error) => {
        console.error('❌ TV Display: Error fetching live status:', error);
      });

      return unsubscribe;
    } catch (err) {
      console.error('❌ TV Display: Exception fetching live status:', err);
      return () => {}; // Return empty function to prevent unsubscribe error
    }
  };

  const fetchRequests = () => {
    if (!profile) return () => {};
    
    try {
      console.log('🔍 TV Display: Fetching requests for profile:', profile.id);
      const requestsRef = collection(db, 'karaoke_requests');
      const q = query(
        requestsRef, 
        where('karaoke_host_id', '==', profile.id),
        where('status', '==', 'approved')
      );
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        console.log('📊 TV Display: Query snapshot received:', querySnapshot.size, 'documents');
        console.log('📊 TV Display: Query snapshot empty:', querySnapshot.empty);
        
        const requestsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('📄 TV Display: Document data:', doc.id, data);
          return {
            id: doc.id,
            ...data
          };
        });
        
        // Sort by paid requests first, then by approved_at timestamp (matching Live Dashboard logic)
        const sortedRequests = requestsData.sort((a, b) => {
          // First, prioritize paid requests (skip_queue = true)
          if (a.skip_queue && !b.skip_queue) return -1;
          if (!a.skip_queue && b.skip_queue) return 1;
          
          // If both have the same paid status, sort by approval time
          const aTime = a.approved_at ? new Date(a.approved_at).getTime() : 0;
          const bTime = b.approved_at ? new Date(b.approved_at).getTime() : 0;
          return aTime - bTime; // Oldest first (first approved = first in queue)
        });
        
        console.log('✅ TV Display: Requests fetched successfully:', sortedRequests.length, 'requests');
        console.log('📋 TV Display: Final requests data:', sortedRequests);
        setRequests(sortedRequests);
      }, (error) => {
        console.error('❌ TV Display: Error fetching requests:', error);
      });

      return unsubscribe;
    } catch (err) {
      console.error('❌ TV Display: Exception fetching requests:', err);
      return () => {}; // Return empty function to prevent unsubscribe error
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-purple-200 mb-4">Profile Not Found</h1>
          <p className="text-purple-300">The karaoke host you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{profile.artist_name} - Karaoke TV Display - Your Song Request</title>
        <meta name="description" content={`Watch ${profile.artist_name}'s karaoke session live`} />
        <style>
          {`
            @keyframes ticker {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
          `}
        </style>
      </Helmet>
      
      <div 
        className="min-h-screen text-white tv-optimized relative overflow-hidden"
        style={settings.preset === 'christmas' ? {
          background: '#0a0e1a',
          position: 'relative',
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(220, 38, 38, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(22, 163, 74, 0.1) 0%, transparent 50%), radial-gradient(circle at 50% 20%, rgba(251, 191, 36, 0.08) 0%, transparent 40%)'
        } : {
          background: `linear-gradient(to bottom right, ${settings.primaryColor}95, ${settings.secondaryColor}95, ${settings.accentColor}95)`
        }}
      >
        {/* ✅ CHRISTMAS: Starry Night Sky Background — z-0 so content/QR stays above */}
        {settings.preset === 'christmas' && (
          <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            {/* Twinkling Stars */}
            {Array.from({ length: 50 }, (_, i) => (
              <div
                key={`star-${i}`}
                className="absolute rounded-full animate-twinkle"
                style={{
                  left: `${(i * 37) % 100}%`,
                  top: `${(i * 23) % 100}%`,
                  width: `${2 + (i % 3)}px`,
                  height: `${2 + (i % 3)}px`,
                  backgroundColor: '#ffffff',
                  boxShadow: '0 0 4px rgba(255, 255, 255, 0.8)',
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${2 + (i % 3)}s`
                }}
              />
            ))}
            
            {/* Moon glow */}
            <div 
              className="absolute top-10 right-20 w-32 h-32 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(251, 191, 36, 0.2) 0%, transparent 70%)',
                boxShadow: '0 0 60px rgba(251, 191, 36, 0.3)',
                animation: 'pulse-slow 4s ease-in-out infinite'
              }}
            />
            
            <style>{`
              @keyframes twinkle {
                0%, 100% { opacity: 0.3; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.2); }
              }
              @keyframes pulse-slow {
                0%, 100% { opacity: 0.3; }
                50% { opacity: 0.6; }
              }
              .animate-twinkle {
                animation: twinkle ease-in-out infinite;
              }
            `}</style>
          </div>
        )}
        {/* Animated Background Elements */}
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          {/* ✅ CHRISTMAS THEME: HEAVY SNOWSTORM EFFECT */}
          {settings.preset === 'christmas' && (
            <>
              {/* Layer 1: Large snowflakes - closest to camera */}
              {Array.from({ length: 100 }, (_, i) => (
                <div
                  key={`snow-close-${i}`}
                  className="absolute text-white animate-snowfall-close"
                  style={{
                    left: `${(i * 19) % 100}%`,
                    animationDelay: `${i * 0.15}s`,
                    animationDuration: `${3 + (i % 4)}s`,
                    fontSize: `${14 + (i % 6)}px`,
                    opacity: 0.95,
                    zIndex: 100
                  }}
                >
                  ❄️
                </div>
              ))}
              
              {/* Layer 2: Medium snowflakes */}
              {Array.from({ length: 80 }, (_, i) => (
                <div
                  key={`snow-mid-${i}`}
                  className="absolute text-white animate-snowfall-mid"
                  style={{
                    left: `${(i * 27) % 100}%`,
                    animationDelay: `${i * 0.25}s`,
                    animationDuration: `${5 + (i % 5)}s`,
                    fontSize: `${8 + (i % 5)}px`,
                    opacity: 0.8,
                    zIndex: 50
                  }}
                >
                  ❄️
                </div>
              ))}
              
              {/* Layer 3: Far snowflakes - background */}
              {Array.from({ length: 60 }, (_, i) => (
                <div
                  key={`snow-far-${i}`}
                  className="absolute text-white animate-snowfall-far"
                  style={{
                    left: `${(i * 31) % 100}%`,
                    animationDelay: `${i * 0.35}s`,
                    animationDuration: `${7 + (i % 6)}s`,
                    fontSize: `${4 + (i % 4)}px`,
                    opacity: 0.5,
                    zIndex: 10
                  }}
                >
                  ❄️
                </div>
              ))}
              
              {/* Floating snow particles (tiny white dots) */}
              {Array.from({ length: 150 }, (_, i) => (
                <div
                  key={`snow-dot-${i}`}
                  className="absolute rounded-full bg-white animate-snowfall-dot"
                  style={{
                    left: `${(i * 23) % 100}%`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: `${4 + (i % 8)}s`,
                    width: '2px',
                    height: '2px',
                    opacity: 0.7,
                    boxShadow: '0 0 2px rgba(255, 255, 255, 0.8)'
                  }}
                />
              ))}
              <style>{`
                @keyframes snowfall-close {
                  0% {
                    transform: translateY(-50px) translateX(0) rotate(0deg) scale(1);
                    opacity: 0;
                  }
                  5% {
                    opacity: 0.95;
                  }
                  50% {
                    transform: translateX(${Math.random() * 60 - 30}px) rotate(180deg) scale(1.1);
                  }
                  95% {
                    opacity: 0.95;
                  }
                  100% {
                    transform: translateY(calc(100vh + 50px)) translateX(${Math.random() * 80 - 40}px) rotate(360deg) scale(0.9);
                    opacity: 0;
                  }
                }
                @keyframes snowfall-mid {
                  0% {
                    transform: translateY(-40px) translateX(0) rotate(0deg);
                    opacity: 0;
                  }
                  8% {
                    opacity: 0.8;
                  }
                  50% {
                    transform: translateX(${Math.random() * 40 - 20}px) rotate(-180deg);
                  }
                  92% {
                    opacity: 0.8;
                  }
                  100% {
                    transform: translateY(calc(100vh + 40px)) translateX(${Math.random() * 60 - 30}px) rotate(360deg);
                    opacity: 0;
                  }
                }
                @keyframes snowfall-far {
                  0% {
                    transform: translateY(-30px) translateX(0) rotate(0deg);
                    opacity: 0;
                  }
                  10% {
                    opacity: 0.5;
                  }
                  50% {
                    transform: translateX(${Math.random() * 20 - 10}px) rotate(90deg);
                  }
                  90% {
                    opacity: 0.5;
                  }
                  100% {
                    transform: translateY(calc(100vh + 30px)) translateX(${Math.random() * 40 - 20}px) rotate(180deg);
                    opacity: 0;
                  }
                }
                @keyframes snowfall-dot {
                  0% {
                    transform: translateY(-20px) translateX(0);
                    opacity: 0;
                  }
                  10% {
                    opacity: 0.7;
                  }
                  50% {
                    transform: translateX(${Math.random() * 15 - 7.5}px);
                  }
                  90% {
                    opacity: 0.7;
                  }
                  100% {
                    transform: translateY(calc(100vh + 20px)) translateX(${Math.random() * 25 - 12.5}px);
                    opacity: 0;
                  }
                }
                .animate-snowfall-close {
                  animation: snowfall-close linear infinite;
                }
                .animate-snowfall-mid {
                  animation: snowfall-mid linear infinite;
                }
                .animate-snowfall-far {
                  animation: snowfall-far linear infinite;
                }
                .animate-snowfall-dot {
                  animation: snowfall-dot linear infinite;
                }
              `}</style>
            </>
          )}

          {/* Floating Music Notes / Christmas Decorations - Conditional based on settings */}
          {settings.floatingNotes && (
            <>
              {/* ✅ CHRISTMAS: Original Christmas Wonderland Decorations */}
              {settings.preset === 'christmas' ? (
                <>
                  {/* Floating Christmas Trees */}
                  {Array.from({ length: 8 }, (_, i) => (
                    <div 
                      key={`tree-${i}`}
                      className="absolute animate-float-tree"
                      style={{
                        left: `${10 + (i * 12)}%`,
                        bottom: `${-5 + (i % 3) * 2}%`,
                        animationDelay: `${i * 0.5}s`,
                        animationDuration: `${4 + (i % 3)}s`,
                        fontSize: `${40 + (i % 3) * 10}px`,
                        filter: 'drop-shadow(0 0 10px rgba(22, 163, 74, 0.6))',
                        zIndex: 20
                      }}
                    >
                      🎄
                    </div>
                  ))}
                  
                  {/* Floating Presents/Gifts */}
                  {Array.from({ length: 12 }, (_, i) => (
                    <div 
                      key={`gift-${i}`}
                      className="absolute animate-float-gift"
                      style={{
                        left: `${(i * 8) % 100}%`,
                        top: `${10 + (i % 4) * 20}%`,
                        animationDelay: `${i * 0.3}s`,
                        animationDuration: `${5 + (i % 4)}s`,
                        fontSize: `${30 + (i % 3) * 8}px`,
                        opacity: 0.9,
                        zIndex: 25
                      }}
                    >
                      🎁
                    </div>
                  ))}
                  
                  {/* Twinkling Stars */}
                  {Array.from({ length: 15 }, (_, i) => (
                    <div 
                      key={`star-float-${i}`}
                      className="absolute animate-twinkle-star"
                      style={{
                        left: `${(i * 7) % 100}%`,
                        top: `${5 + (i % 5) * 15}%`,
                        animationDelay: `${i * 0.4}s`,
                        animationDuration: `${2 + (i % 3)}s`,
                        fontSize: `${20 + (i % 4) * 5}px`,
                        filter: 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.8))',
                        zIndex: 15
                      }}
                    >
                      ⭐
                    </div>
                  ))}
                  
                  {/* Santa and Reindeer */}
                  <div className="absolute top-15 left-5 animate-santa-sleigh" style={{ fontSize: '35px', zIndex: 30 }}>
                    🎅
                  </div>
                  <div className="absolute top-25 left-8 animate-reindeer" style={{ fontSize: '30px', zIndex: 29, animationDelay: '0.2s' }}>
                    🦌
                  </div>
                  <div className="absolute bottom-15 right-10 animate-float-bell" style={{ fontSize: '40px', zIndex: 28 }}>
                    🔔
                  </div>
                  
                  {/* CHRISTMAS LIGHTS STRINGS - Animated like real lights */}
                  <div className="absolute top-0 left-0 w-full h-6 flex items-center justify-center gap-3" style={{ zIndex: 100 }}>
                    {Array.from({ length: 60 }, (_, i) => (
                      <div
                        key={`string-light-${i}`}
                        className="w-3 h-3 rounded-full animate-light-twinkle"
                        style={{
                          backgroundColor: ['#dc2626', '#16a34a', '#ffffff', '#fbbf24', '#dc2626', '#16a34a'][i % 6],
                          animationDelay: `${i * 0.15}s`,
                          animationDuration: '2s',
                          boxShadow: '0 0 8px currentColor, 0 0 12px currentColor',
                          filter: 'brightness(1.2)'
                        }}
                      />
                    ))}
                  </div>
                  
                  <div className="absolute bottom-0 left-0 w-full h-6 flex items-center justify-center gap-3" style={{ zIndex: 100 }}>
                    {Array.from({ length: 60 }, (_, i) => (
                      <div
                        key={`string-light-bottom-${i}`}
                        className="w-3 h-3 rounded-full animate-light-twinkle"
                        style={{
                          backgroundColor: ['#16a34a', '#dc2626', '#fbbf24', '#ffffff', '#16a34a', '#dc2626'][i % 6],
                          animationDelay: `${i * 0.15 + 1}s`,
                          animationDuration: '2s',
                          boxShadow: '0 0 8px currentColor, 0 0 12px currentColor',
                          filter: 'brightness(1.2)'
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Side garlands with lights */}
                  <div className="absolute left-0 top-1/4 w-8 flex flex-col items-center gap-4" style={{ zIndex: 90 }}>
                    {Array.from({ length: 20 }, (_, i) => (
                      <div
                        key={`garland-left-${i}`}
                        className="w-4 h-4 rounded-full animate-light-twinkle"
                        style={{
                          backgroundColor: ['#dc2626', '#16a34a', '#ffffff', '#fbbf24'][i % 4],
                          animationDelay: `${i * 0.2}s`,
                          boxShadow: '0 0 6px currentColor'
                        }}
                      />
                    ))}
                  </div>
                  
                  <div className="absolute right-0 top-1/4 w-8 flex flex-col items-center gap-4" style={{ zIndex: 90 }}>
                    {Array.from({ length: 20 }, (_, i) => (
                      <div
                        key={`garland-right-${i}`}
                        className="w-4 h-4 rounded-full animate-light-twinkle"
                        style={{
                          backgroundColor: ['#16a34a', '#dc2626', '#fbbf24', '#ffffff'][i % 4],
                          animationDelay: `${i * 0.2 + 0.5}s`,
                          boxShadow: '0 0 6px currentColor'
                        }}
                      />
                    ))}
                  </div>
                  
                  <style>{`
                    @keyframes float-tree {
                      0%, 100% {
                        transform: translateY(0) rotate(0deg);
                      }
                      50% {
                        transform: translateY(-15px) rotate(2deg);
                      }
                    }
                    @keyframes float-gift {
                      0%, 100% {
                        transform: translateY(0) translateX(0) rotate(0deg);
                      }
                      25% {
                        transform: translateY(-10px) translateX(5px) rotate(5deg);
                      }
                      75% {
                        transform: translateY(-5px) translateX(-5px) rotate(-5deg);
                      }
                    }
                    @keyframes twinkle-star {
                      0%, 100% {
                        opacity: 0.6;
                        transform: scale(1);
                      }
                      50% {
                        opacity: 1;
                        transform: scale(1.3);
                      }
                    }
                    @keyframes santa-sleigh {
                      0% {
                        transform: translateX(0) translateY(0);
                      }
                      33% {
                        transform: translateX(10px) translateY(-5px);
                      }
                      66% {
                        transform: translateX(-5px) translateY(5px);
                      }
                      100% {
                        transform: translateX(0) translateY(0);
                      }
                    }
                    @keyframes reindeer {
                      0%, 100% {
                        transform: translateX(0) rotate(0deg);
                      }
                      50% {
                        transform: translateX(8px) rotate(-3deg);
                      }
                    }
                    @keyframes float-bell {
                      0%, 100% {
                        transform: translateY(0) rotate(0deg);
                      }
                      50% {
                        transform: translateY(-12px) rotate(15deg);
                      }
                    }
                    @keyframes light-twinkle {
                      0%, 100% {
                        opacity: 0.4;
                        transform: scale(0.9);
                      }
                      50% {
                        opacity: 1;
                        transform: scale(1.1);
                      }
                    }
                    .animate-float-tree {
                      animation: float-tree ease-in-out infinite;
                    }
                    .animate-float-gift {
                      animation: float-gift ease-in-out infinite;
                    }
                    .animate-twinkle-star {
                      animation: twinkle-star ease-in-out infinite;
                    }
                    .animate-santa-sleigh {
                      animation: santa-sleigh ease-in-out infinite;
                    }
                    .animate-reindeer {
                      animation: reindeer ease-in-out infinite;
                    }
                    .animate-float-bell {
                      animation: float-bell ease-in-out infinite;
                    }
                    .animate-light-twinkle {
                      animation: light-twinkle ease-in-out infinite;
                    }
                  `}</style>
                </>
              ) : (
                <>
                  <div className="absolute top-20 left-10 animate-bounce" style={{ 
                    animationDelay: '0s', 
                    animationDuration: `${6 - settings.animationSpeed}s`,
                    opacity: settings.noteOpacity
                  }}>
                    <span className="text-4xl" style={{ color: settings.primaryColor }}>♪</span>
                  </div>
                  <div className="absolute top-40 right-20 animate-bounce" style={{ 
                    animationDelay: '1s', 
                    animationDuration: `${7 - settings.animationSpeed}s`,
                    opacity: settings.noteOpacity
                  }}>
                    <span className="text-3xl" style={{ color: settings.secondaryColor }}>♫</span>
                  </div>
                  <div className="absolute top-60 left-1/4 animate-bounce" style={{ 
                    animationDelay: '2s', 
                    animationDuration: `${6.5 - settings.animationSpeed}s`,
                    opacity: settings.noteOpacity
                  }}>
                    <span className="text-2xl" style={{ color: settings.accentColor }}>♬</span>
                  </div>
              <div className="absolute top-80 right-1/3 animate-bounce" style={{ 
                animationDelay: '0.5s', 
                animationDuration: `${7.5 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity
              }}>
                <span className="text-3xl" style={{ color: settings.primaryColor }}>♪</span>
              </div>
              <div className="absolute top-32 left-2/3 animate-bounce" style={{ 
                animationDelay: '1.5s', 
                animationDuration: `${6.2 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity
              }}>
                <span className="text-2xl" style={{ color: settings.secondaryColor }}>♫</span>
              </div>
              
              {/* Bottom Floating Notes */}
              <div className="absolute bottom-40 left-20 animate-bounce" style={{ 
                animationDelay: '2.5s', 
                animationDuration: `${6.8 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity
              }}>
                <span className="text-3xl" style={{ color: settings.secondaryColor }}>♬</span>
              </div>
              <div className="absolute bottom-60 right-10 animate-bounce" style={{ 
                animationDelay: '0.8s', 
                animationDuration: `${7.2 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity
              }}>
                <span className="text-2xl" style={{ color: settings.accentColor }}>♪</span>
              </div>
              <div className="absolute bottom-80 left-1/3 animate-bounce" style={{ 
                animationDelay: '1.8s', 
                animationDuration: `${6.6 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity
              }}>
                <span className="text-3xl" style={{ color: settings.primaryColor }}>♫</span>
              </div>
              
              {/* Additional Floating Elements */}
              <div className="absolute top-1/2 left-16 animate-bounce" style={{ 
                animationDelay: '3s', 
                animationDuration: `${8 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity * 0.75
              }}>
                <span className="text-2xl" style={{ color: settings.accentColor }}>🎵</span>
              </div>
              <div className="absolute top-1/3 right-1/4 animate-bounce" style={{ 
                animationDelay: '2.2s', 
                animationDuration: `${7.8 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity * 0.75
              }}>
                <span className="text-xl" style={{ color: settings.primaryColor }}>🎶</span>
              </div>
              <div className="absolute bottom-1/3 left-1/5 animate-bounce" style={{ 
                animationDelay: '1.3s', 
                animationDuration: `${6.9 - settings.animationSpeed}s`,
                opacity: settings.noteOpacity * 0.75
              }}>
                <span className="text-2xl" style={{ color: settings.secondaryColor }}>🎤</span>
              </div>
                </>
              )}
            </>
          )}
          
          {/* Animated Wave Lines - Conditional based on settings */}
          {settings.waveLines && (
            <>
              <div className="absolute top-0 left-0 w-full h-1 animate-pulse" style={{ 
                background: `linear-gradient(to right, transparent, ${settings.primaryColor}20, transparent)`,
                animationDelay: '0s',
                animationDuration: `${6 - settings.animationSpeed}s`
              }}></div>
              <div className="absolute top-8 left-0 w-full h-1 animate-pulse" style={{ 
                background: `linear-gradient(to right, transparent, ${settings.secondaryColor}20, transparent)`,
                animationDelay: '1s',
                animationDuration: `${6 - settings.animationSpeed}s`
              }}></div>
              <div className="absolute top-16 left-0 w-full h-1 animate-pulse" style={{ 
                background: `linear-gradient(to right, transparent, ${settings.accentColor}20, transparent)`,
                animationDelay: '2s',
                animationDuration: `${6 - settings.animationSpeed}s`
              }}></div>
              
              {/* Bottom Wave Lines */}
              <div className="absolute bottom-0 left-0 w-full h-1 animate-pulse" style={{ 
                background: `linear-gradient(to right, transparent, ${settings.primaryColor}20, transparent)`,
                animationDelay: '0.5s',
                animationDuration: `${6 - settings.animationSpeed}s`
              }}></div>
              <div className="absolute bottom-8 left-0 w-full h-1 animate-pulse" style={{ 
                background: `linear-gradient(to right, transparent, ${settings.secondaryColor}20, transparent)`,
                animationDelay: '1.5s',
                animationDuration: `${6 - settings.animationSpeed}s`
              }}></div>
              <div className="absolute bottom-16 left-0 w-full h-1 animate-pulse" style={{ 
                background: `linear-gradient(to right, transparent, ${settings.accentColor}20, transparent)`,
                animationDelay: '2.5s',
                animationDuration: `${6 - settings.animationSpeed}s`
              }}></div>
            </>
          )}
          
          {/* Subtle Particle Effects - Conditional and density-based */}
          {settings.particles && (
            <>
              {Array.from({ length: settings.particleDensity * 2 }, (_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full animate-ping"
                  style={{
                    top: `${20 + (i * 15) % 80}%`,
                    left: `${10 + (i * 20) % 80}%`,
                    width: `${1 + (i % 2)}px`,
                    height: `${1 + (i % 2)}px`,
                    backgroundColor: [settings.primaryColor, settings.secondaryColor, settings.accentColor][i % 3] + '30',
                    animationDelay: `${i * 0.5}s`,
                    animationDuration: `${2 + (i % 3)}s`,
                    opacity: settings.noteOpacity * 1.5
                  }}
                ></div>
              ))}
            </>
          )}
          
          {/* Dynamic Color Shifts - Conditional based on settings */}
          {settings.colorShifts && (
            <div 
              className="absolute inset-0 animate-pulse opacity-50" 
              style={{ 
                animationDuration: `${8 - settings.animationSpeed}s`,
                background: `linear-gradient(to bottom right, ${settings.primaryColor}95, ${settings.secondaryColor}95, ${settings.accentColor}95)`
              }}
            ></div>
          )}
          
          {/* Subtle Grid Pattern - Conditional based on settings */}
          {settings.gridPattern && (
            <div className="absolute inset-0 opacity-5">
              <div className="w-full h-full" style={{
                backgroundImage: `
                  linear-gradient(${settings.primaryColor}20 1px, transparent 1px),
                  linear-gradient(90deg, ${settings.primaryColor}20 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px'
              }}></div>
            </div>
          )}
        </div>
        {/* Header — above decorative layers */}
        <header className={`relative z-10 border-b backdrop-blur-sm ${
          settings.preset === 'christmas'
            ? 'border-red-600/50 bg-red-950/30'
            : 'border-purple-800/50 bg-purple-900/30'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              {/* YSR Logo - Top Left - Fixed positioning to prevent overlap */}
              <div className="flex items-center space-x-2 flex-shrink-0 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0 ${
                  settings.preset === 'christmas'
                    ? 'bg-gradient-to-br from-red-600 via-green-600 to-yellow-600'
                    : 'bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600'
                }`}>
                  {settings.preset === 'christmas' ? (
                    <span className="text-xl">🎄</span>
                  ) : (
                    <span className="text-white font-bold text-sm">YSR</span>
                  )}
                </div>
                <div className="text-white font-bold text-sm truncate hidden sm:block">Your Song Request</div>
              </div>
              
              {/* Host Name - Center - Dynamic positioning */}
              <div className="flex items-center space-x-3 flex-1 justify-center min-w-0 px-2 sm:px-4">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center flex-shrink-0 ${
                  settings.preset === 'christmas'
                    ? 'bg-red-600/20'
                    : 'bg-purple-600/20'
                }`}>
                  {settings.preset === 'christmas' ? (
                    <span className="text-3xl sm:text-4xl">🎅</span>
                  ) : (
                    <Mic className={`w-6 h-6 sm:w-8 sm:h-8 ${
                      settings.preset === 'christmas' ? 'text-red-400' : 'text-purple-400'
                    }`} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold truncate ${
                    settings.preset === 'christmas' ? 'text-red-100' : 'text-purple-100'
                  }`}>{profile.artist_name}</h1>
                  <p className={`text-sm sm:text-lg lg:text-xl truncate ${
                    settings.preset === 'christmas' ? 'text-green-300' : 'text-purple-300'
                  }`}>Karaoke Host {settings.preset === 'christmas' && '🎄'}</p>
                </div>
              </div>
              
              {/* Live Status Indicator and Fullscreen Button - Right */}
              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                {isLive ? (
                  <motion.div 
                    className="flex items-center space-x-2 bg-red-600/20 border border-red-500/50 rounded-full px-4 py-2"
                    animate={{ 
                      scale: [1, 1.05, 1],
                      boxShadow: [
                        "0 0 0 rgba(239, 68, 68, 0.4)",
                        "0 0 20px rgba(239, 68, 68, 0.6)",
                        "0 0 0 rgba(239, 68, 68, 0.4)"
                      ]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-red-300 font-semibold">LIVE NOW</span>
                  </motion.div>
                ) : (
                  <div className="flex items-center space-x-2 bg-gray-600/20 border border-gray-500/50 rounded-full px-4 py-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="text-gray-300">OFFLINE</span>
                  </div>
                )}
                
                {/* Fullscreen Button - Less noticeable when in fullscreen */}
                <Button
                  onClick={handleFullscreenToggle}
                  variant="outline"
                  size="sm"
                  className={isFullscreen 
                    ? "border-white/30 bg-gradient-to-b from-white/10 to-transparent text-white/80 hover:bg-white/20 hover:text-white text-xs opacity-60 hover:opacity-100 transition-opacity"
                    : "border-purple-700 text-purple-300 hover:bg-purple-800"
                  }
                >
                  {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Up Next Announcement Overlay */}
        {upNextAnnouncement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 backdrop-blur-md"
            style={{
              pointerEvents: 'none',
              background: settings.preset === 'christmas'
                ? 'radial-gradient(circle at center, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.9))'
                : 'rgba(0, 0, 0, 0.8)'
            }}
          >
            {/* ✅ CHRISTMAS: IMMERSIVE Snowstorm on announcement */}
            {settings.preset === 'christmas' && (
              <>
                {/* Heavy snow layers */}
                {Array.from({ length: 50 }, (_, i) => (
                  <div
                    key={`announce-snow-heavy-${i}`}
                    className="absolute text-white animate-snowfall-announce-heavy"
                    style={{
                      left: `${(i * 20) % 100}%`,
                      animationDelay: `${i * 0.15}s`,
                      animationDuration: `${3 + (i % 4)}s`,
                      fontSize: `${14 + (i % 8)}px`,
                      opacity: 0.95,
                      zIndex: 51
                    }}
                  >
                    ❄️
                  </div>
                ))}
                {Array.from({ length: 40 }, (_, i) => (
                  <div
                    key={`announce-snow-medium-${i}`}
                    className="absolute text-white animate-snowfall-announce-medium"
                    style={{
                      left: `${(i * 27) % 100}%`,
                      animationDelay: `${i * 0.25}s`,
                      animationDuration: `${5 + (i % 5)}s`,
                      fontSize: `${8 + (i % 6)}px`,
                      opacity: 0.8,
                      zIndex: 50
                    }}
                  >
                    ❄️
                  </div>
                ))}
                {Array.from({ length: 60 }, (_, i) => (
                  <div
                    key={`announce-snow-dots-${i}`}
                    className="absolute rounded-full bg-white animate-snowfall-announce-dot"
                    style={{
                      left: `${(i * 19) % 100}%`,
                      animationDelay: `${i * 0.1}s`,
                      animationDuration: `${4 + (i % 6)}s`,
                      width: '3px',
                      height: '3px',
                      opacity: 0.7,
                      boxShadow: '0 0 3px rgba(255, 255, 255, 0.9)',
                      zIndex: 49
                    }}
                  />
                ))}
                
                {/* Animated Christmas lights border around announcement */}
                <div className="absolute top-10 left-10 right-10 h-12 flex items-center justify-center gap-2" style={{ zIndex: 52 }}>
                  {Array.from({ length: 80 }, (_, i) => (
                    <div
                      key={`announce-light-top-${i}`}
                      className="w-3 h-3 rounded-full animate-light-announce"
                      style={{
                        backgroundColor: ['#dc2626', '#16a34a', '#ffffff', '#fbbf24', '#dc2626', '#16a34a'][i % 6],
                        animationDelay: `${i * 0.1}s`,
                        animationDuration: '1.5s',
                        boxShadow: '0 0 10px currentColor, 0 0 15px currentColor',
                        filter: 'brightness(1.4)'
                      }}
                    />
                  ))}
                </div>
                
                <div className="absolute bottom-10 left-10 right-10 h-12 flex items-center justify-center gap-2" style={{ zIndex: 52 }}>
                  {Array.from({ length: 80 }, (_, i) => (
                    <div
                      key={`announce-light-bottom-${i}`}
                      className="w-3 h-3 rounded-full animate-light-announce"
                      style={{
                        backgroundColor: ['#16a34a', '#dc2626', '#fbbf24', '#ffffff', '#16a34a', '#dc2626'][i % 6],
                        animationDelay: `${i * 0.1 + 0.75}s`,
                        animationDuration: '1.5s',
                        boxShadow: '0 0 10px currentColor, 0 0 15px currentColor',
                        filter: 'brightness(1.4)'
                      }}
                    />
                  ))}
                </div>
                
                <style>{`
                  @keyframes snowfall-announce-heavy {
                    0% {
                      transform: translateY(-40px) translateX(0) rotate(0deg) scale(1);
                      opacity: 0;
                    }
                    5% {
                      opacity: 0.95;
                    }
                    50% {
                      transform: translateX(${Math.random() * 50 - 25}px) rotate(180deg) scale(1.2);
                    }
                    95% {
                      opacity: 0.95;
                    }
                    100% {
                      transform: translateY(calc(100vh + 40px)) translateX(${Math.random() * 70 - 35}px) rotate(360deg) scale(0.8);
                      opacity: 0;
                    }
                  }
                  @keyframes snowfall-announce-medium {
                    0% {
                      transform: translateY(-35px) translateX(0) rotate(0deg);
                      opacity: 0;
                    }
                    8% {
                      opacity: 0.8;
                    }
                    50% {
                      transform: translateX(${Math.random() * 35 - 17.5}px) rotate(-180deg);
                    }
                    92% {
                      opacity: 0.8;
                    }
                    100% {
                      transform: translateY(calc(100vh + 35px)) translateX(${Math.random() * 50 - 25}px) rotate(360deg);
                      opacity: 0;
                    }
                  }
                  @keyframes snowfall-announce-dot {
                    0% {
                      transform: translateY(-25px) translateX(0);
                      opacity: 0;
                    }
                    10% {
                      opacity: 0.7;
                    }
                    50% {
                      transform: translateX(${Math.random() * 20 - 10}px);
                    }
                    90% {
                      opacity: 0.7;
                    }
                    100% {
                      transform: translateY(calc(100vh + 25px)) translateX(${Math.random() * 30 - 15}px);
                      opacity: 0;
                    }
                  }
                  @keyframes light-announce {
                    0%, 100% {
                      opacity: 0.6;
                      transform: scale(1);
                    }
                    50% {
                      opacity: 1;
                      transform: scale(1.2);
                    }
                  }
                  .animate-snowfall-announce-heavy {
                    animation: snowfall-announce-heavy linear infinite;
                  }
                  .animate-snowfall-announce-medium {
                    animation: snowfall-announce-medium linear infinite;
                  }
                  .animate-snowfall-announce-dot {
                    animation: snowfall-announce-dot linear infinite;
                  }
                  .animate-light-announce {
                    animation: light-announce ease-in-out infinite;
                  }
                `}</style>
              </>
            )}
            
            <div className="flex flex-col items-center justify-center h-full text-center px-4 relative z-50">
              {/* ✅ CHRISTMAS: Enhanced UP NEXT Title */}
              <motion.h2
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className={`text-7xl font-bold mb-10 ${
                  settings.preset === 'christmas' 
                    ? 'bg-gradient-to-r from-red-400 via-green-400 to-yellow-400 bg-clip-text text-transparent animate-christmas-title' 
                    : 'text-purple-100'
                }`}
                style={settings.preset === 'christmas' ? {
                  filter: 'drop-shadow(0 0 20px rgba(220, 38, 38, 0.8)) drop-shadow(0 0 30px rgba(22, 163, 74, 0.6))',
                  textShadow: '0 0 40px rgba(251, 191, 36, 0.5)'
                } : {}}
              >
                {settings.preset === 'christmas' ? (
                  <>
                    <span className="inline-block animate-bounce-slow">🎄</span>
                    <span className="mx-4">UP NEXT</span>
                    <span className="inline-block animate-bounce-slow-reverse">🎄</span>
                  </>
                ) : 'UP NEXT'}
              </motion.h2>
              
              {/* Song Title with Enhanced Christmas Gradient */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                className="text-8xl font-bold mb-8"
                style={{
                  background: settings.preset === 'christmas' 
                    ? 'linear-gradient(135deg, #ffffff, #dc2626, #ffffff, #16a34a, #ffffff, #fbbf24, #ffffff)'
                    : combinedColorSchemes[announcementColorScheme].songTitleGradient,
                  backgroundSize: settings.preset === 'christmas' ? '200% 200%' : '100% 100%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: settings.preset === 'christmas' 
                    ? 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 50px rgba(220, 38, 38, 0.6))'
                    : 'none',
                  animation: settings.preset === 'christmas' ? 'christmas-gradient-shift 4s ease infinite' : 'none'
                }}
              >
                {capitalizeWords(upNextAnnouncement.song_title) || 'Unknown Song'}
              </motion.div>
              
              {/* Artist Name with Christmas styling */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                className={`text-4xl mb-10 ${
                  settings.preset === 'christmas' 
                    ? 'text-white font-semibold animate-pulse-slow' 
                    : 'text-purple-300'
                }`}
                style={settings.preset === 'christmas' ? {
                  textShadow: '0 0 20px rgba(22, 163, 74, 0.8), 0 0 40px rgba(22, 163, 74, 0.5)'
                } : {}}
              >
                by {capitalizeWords(upNextAnnouncement.artist_name) || 'Unknown Artist'}
              </motion.div>
              
              {/* Enhanced Requester Name Box with Christmas styling */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.9, ease: "easeOut" }}
                className={`bg-gradient-to-r rounded-full px-12 py-4 mb-16 shadow-2xl ${
                  settings.preset === 'christmas'
                    ? 'from-red-600 via-yellow-500 to-green-600 animate-pulse-glow'
                    : `${combinedColorSchemes[announcementColorScheme].requesterBoxFrom} ${combinedColorSchemes[announcementColorScheme].requesterBoxTo}`
                }`}
                style={settings.preset === 'christmas' ? {
                  boxShadow: '0 0 40px rgba(220, 38, 38, 0.8), 0 0 60px rgba(251, 191, 36, 0.6), 0 0 80px rgba(22, 163, 74, 0.4)',
                  border: '3px solid rgba(255, 255, 255, 0.3)'
                } : {}}
              >
                <div className="text-center">
                  <div className={`text-lg mb-2 ${
                    settings.preset === 'christmas' ? 'text-white font-semibold' : combinedColorSchemes[announcementColorScheme].requesterTextColor
                  }`}>
                    {settings.preset === 'christmas' ? '🎅 requested by 🎅' : 'requested by'}
                  </div>
                  <div className="text-3xl font-bold text-white" style={settings.preset === 'christmas' ? {
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.8)'
                  } : {}}>
                    {capitalizeWords(upNextAnnouncement.requester_name) || 'Anonymous'}
                  </div>
                </div>
              </motion.div>
              
              {/* ✅ CHRISTMAS: OVERWHELMING Christmas Decorations Around Announcement */}
              {settings.preset === 'christmas' && (
                <>
                  {/* Surrounding Christmas elements */}
                  {Array.from({ length: 20 }, (_, i) => (
                    <div
                      key={`announce-float-${i}`}
                      className="absolute animate-announce-float"
                      style={{
                        left: `${5 + (i * 4.5)}%`,
                        top: `${10 + (i % 5) * 15}%`,
                        fontSize: `${30 + (i % 4) * 8}px`,
                        animationDelay: `${i * 0.3}s`,
                        animationDuration: `${3 + (i % 3)}s`,
                        zIndex: 53,
                        filter: 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.6))'
                      }}
                    >
                      {['🎄', '🎁', '⭐', '🔔', '❄️'][i % 5]}
                    </div>
                  ))}
                  
                  {/* Main center decorations */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
                    className="flex items-center justify-center space-x-6"
                  >
                    <motion.div
                      animate={{ 
                        rotate: [0, 25, -25, 0],
                        scale: [1, 1.4, 1],
                        y: [0, -20, 0]
                      }}
                      transition={{ 
                        duration: 2.5, 
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="text-9xl"
                      style={{
                        filter: 'drop-shadow(0 0 25px rgba(220, 38, 38, 0.9)) drop-shadow(0 0 40px rgba(22, 163, 74, 0.6))'
                      }}
                    >
                      🎄
                    </motion.div>
                    <motion.div
                      animate={{ 
                        rotate: [0, -25, 25, 0],
                        scale: [1, 1.5, 1],
                        y: [0, -25, 0]
                      }}
                      transition={{ 
                        duration: 3, 
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.3
                      }}
                      className="text-[10rem]"
                      style={{
                        filter: 'drop-shadow(0 0 35px rgba(251, 191, 36, 1)) drop-shadow(0 0 50px rgba(251, 191, 36, 0.7))'
                      }}
                    >
                      ⭐
                    </motion.div>
                    <motion.div
                      animate={{ 
                        rotate: [0, 20, -20, 0],
                        scale: [1, 1.4, 1],
                        y: [0, -20, 0]
                      }}
                      transition={{ 
                        duration: 2.5, 
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.6
                      }}
                      className="text-9xl"
                      style={{
                        filter: 'drop-shadow(0 0 25px rgba(22, 163, 74, 0.9)) drop-shadow(0 0 40px rgba(220, 38, 38, 0.6))'
                      }}
                    >
                      🎁
                    </motion.div>
                    <motion.div
                      animate={{ 
                        rotate: [0, -20, 20, 0],
                        scale: [1, 1.3, 1],
                        y: [0, -15, 0]
                      }}
                      transition={{ 
                        duration: 2.2, 
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.9
                      }}
                      className="text-8xl"
                      style={{
                        filter: 'drop-shadow(0 0 20px rgba(220, 38, 38, 0.8)) drop-shadow(0 0 30px rgba(251, 191, 36, 0.5))'
                      }}
                    >
                      🎅
                    </motion.div>
                    <motion.div
                      animate={{ 
                        rotate: [0, 25, -25, 0],
                        scale: [1, 1.4, 1],
                        y: [0, -20, 0]
                      }}
                      transition={{ 
                        duration: 2.5, 
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1.2
                      }}
                      className="text-9xl"
                      style={{
                        filter: 'drop-shadow(0 0 25px rgba(22, 163, 74, 0.9)) drop-shadow(0 0 40px rgba(251, 191, 36, 0.6))'
                      }}
                    >
                      🦌
                    </motion.div>
                    <motion.div
                      animate={{ 
                        rotate: [0, -15, 15, 0],
                        scale: [1, 1.2, 1],
                        y: [0, -12, 0]
                      }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1.5
                      }}
                      className="text-7xl"
                      style={{
                        filter: 'drop-shadow(0 0 18px rgba(251, 191, 36, 0.8))'
                      }}
                    >
                      🔔
                    </motion.div>
                  </motion.div>
                  
                  <style>{`
                    @keyframes announce-float {
                      0%, 100% {
                        transform: translateY(0) rotate(0deg);
                        opacity: 0.7;
                      }
                      50% {
                        transform: translateY(-15px) rotate(10deg);
                        opacity: 1;
                      }
                    }
                    .animate-announce-float {
                      animation: announce-float ease-in-out infinite;
                    }
                  `}</style>
                </>
              )}
              
              {/* Regular decorations for non-Christmas */}
              {settings.preset !== 'christmas' && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
                  className="flex items-center justify-center space-x-8"
                >
                  <motion.div
                    animate={{ 
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      duration: 3, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="text-6xl"
                  >
                    🎵
                  </motion.div>
                  <motion.div
                    animate={{ 
                      rotate: [0, -10, 10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      duration: 3, 
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5
                    }}
                    className="text-6xl"
                  >
                    🎶
                  </motion.div>
                  <motion.div
                    animate={{ 
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.2, 1]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1
                    }}
                    className="text-6xl"
                  >
                    🎤
                  </motion.div>
                </motion.div>
              )}
              
              {/* ✅ CHRISTMAS: Additional animated elements */}
              {settings.preset === 'christmas' && (
                <style>{`
                  @keyframes christmas-title {
                    0%, 100% {
                      filter: drop-shadow(0 0 20px rgba(220, 38, 38, 0.8)) drop-shadow(0 0 30px rgba(22, 163, 74, 0.6));
                    }
                    50% {
                      filter: drop-shadow(0 0 30px rgba(22, 163, 74, 0.8)) drop-shadow(0 0 40px rgba(251, 191, 36, 0.6));
                    }
                  }
                  @keyframes bounce-slow {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                  }
                  @keyframes bounce-slow-reverse {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(10px); }
                  }
                  @keyframes pulse-slow {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                  }
                  @keyframes pulse-glow {
                    0%, 100% {
                      box-shadow: 0 0 40px rgba(220, 38, 38, 0.8), 0 0 60px rgba(251, 191, 36, 0.6), 0 0 80px rgba(22, 163, 74, 0.4);
                    }
                    50% {
                      box-shadow: 0 0 60px rgba(22, 163, 74, 0.8), 0 0 80px rgba(220, 38, 38, 0.6), 0 0 100px rgba(251, 191, 36, 0.4);
                    }
                  }
                  @keyframes christmas-gradient-shift {
                    0% {
                      background-position: 0% 50%;
                    }
                    50% {
                      background-position: 100% 50%;
                    }
                    100% {
                      background-position: 0% 50%;
                    }
                  }
                  .animate-christmas-title {
                    animation: christmas-title 2s ease-in-out infinite;
                  }
                  .animate-bounce-slow {
                    animation: bounce-slow 2s ease-in-out infinite;
                  }
                  .animate-bounce-slow-reverse {
                    animation: bounce-slow-reverse 2s ease-in-out infinite;
                  }
                  .animate-pulse-slow {
                    animation: pulse-slow 3s ease-in-out infinite;
                  }
                  .animate-pulse-glow {
                    animation: pulse-glow 2s ease-in-out infinite;
                  }
                `}</style>
              )}
            </div>
          </motion.div>
        )}

        <main className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${tickerSettings.enabled && tickerSettings.text ? 'pb-24' : ''}`}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Main Content - Live Status */}
            <div className="relative z-10 lg:col-span-2">
              {isLive ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Live Session Info - Compact */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="mb-4"
                  >
                    <div 
                      className="glass-effect border border-purple-600/50 rounded-lg p-3"
                      style={{
                        backgroundColor: `rgba(0, 0, 0, ${settings.cardTransparency})`,
                        backdropFilter: settings.backdropBlur ? 'blur(10px)' : 'none'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          >
                            <Radio className="w-5 h-5 text-red-400" />
                          </motion.div>
                          <span className="text-lg font-semibold text-purple-100">Live Session</span>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center space-x-2">
                            <Users className="w-5 h-5 text-purple-400" />
                            <span className="text-xl font-bold text-purple-100">{requests.length}</span>
                            <span className="text-sm text-purple-300">Songs</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-5 h-5 text-purple-400" />
                            <span className="text-lg font-semibold text-purple-100">
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Live Request Queue */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card 
                      className="glass-effect border-purple-600"
                      style={{
                        backgroundColor: `rgba(0, 0, 0, ${settings.cardTransparency})`,
                        backdropFilter: settings.backdropBlur ? 'blur(10px)' : 'none'
                      }}
                    >
                      <CardHeader>
                        <CardTitle className="text-2xl text-purple-100 flex items-center">
                          <motion.div
                            animate={{ rotate: [0, 5, -5, 0] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          >
                            <Music className="mr-2" />
                          </motion.div>
                          Live Request Queue
                        </CardTitle>
                        <CardDescription className="text-purple-300 text-lg">
                          Songs requested by the audience during this live session
                        </CardDescription>
                      </CardHeader>
                      <CardContent className={tickerSettings.enabled && tickerSettings.text ? 'pb-6' : ''}>
                        {settings.showRequestQueue ? (
                          requests.length > 0 ? (
                            <div className="space-y-4">
                              <AnimatePresence mode="popLayout">
                                {(() => {
                                  // Determine which requests to show based on scroll state
                                  let requestsToShow = [];
                                  if (requests.length <= 3) {
                                    // Always show all if 3 or fewer
                                    requestsToShow = requests;
                                  } else if (queueScrollDirection === 'static' && queueScrollIndex === 0) {
                                    // Show top 3 statically
                                    requestsToShow = requests.slice(0, 3);
                                  } else {
                                    // Show 3 requests starting from scrollIndex
                                    requestsToShow = requests.slice(queueScrollIndex, queueScrollIndex + 3);
                                  }
                                  
                                  return requestsToShow.map((request, displayIndex) => {
                                    const actualIndex = requests.length <= 3 
                                      ? displayIndex 
                                      : (queueScrollDirection === 'static' && queueScrollIndex === 0)
                                        ? displayIndex
                                        : (queueScrollIndex + displayIndex);
                                    return (
                                      <motion.div
                                        key={request.id}
                                        layout
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ 
                                          opacity: 0, 
                                          y: -20,
                                          transition: { duration: 0.3, ease: "easeInOut" }
                                        }}
                                        transition={{ 
                                          duration: 0.5,
                                          layout: { duration: 0.5, ease: "easeInOut" }
                                        }}
                                        className="bg-purple-800/30 border border-purple-700/50 rounded-lg p-6 cursor-pointer"
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-4 flex-1 min-w-0">
                                            <motion.div 
                                              className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-lg font-bold text-white flex-shrink-0"
                                              whileHover={{ scale: 1.1, rotate: 360 }}
                                              transition={{ duration: 0.3 }}
                                            >
                                              {actualIndex + 1}
                                            </motion.div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xl text-purple-100 font-semibold truncate">
                                          {capitalizeWords(request.song_title) || 'Song Request'}
                                        </div>
                                        {request.artist_name && (
                                          <div className="text-lg text-purple-300 truncate">
                                            by {capitalizeWords(request.artist_name)}
                                          </div>
                                        )}
                                        {/* Show Singer Name by Default */}
                                        {request.requester_name && (
                                          <div className="text-base text-purple-200 font-medium mt-1 truncate">
                                            🎤 {capitalizeWords(request.requester_name)}
                                          </div>
                                        )}
                                      </div>
                                          </div>
                                        </div>
                                      </motion.div>
                                    );
                                  });
                                })()}
                              </AnimatePresence>
                              {requests.length > 3 && (
                                <div className={`text-center text-purple-300 text-lg ${tickerSettings.enabled && tickerSettings.text ? 'mt-4 mb-2' : 'mt-6 mb-4'}`}>
                                  +{requests.length - 3} more {requests.length - 3 === 1 ? 'song' : 'songs'} in the queue
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <motion.div
                                animate={{ 
                                  rotate: [0, 10, -10, 0],
                                  scale: [1, 1.1, 1]
                                }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                              >
                                <Music className="w-20 h-20 text-purple-400 mx-auto mb-6 opacity-50" />
                              </motion.div>
                              <p className="text-2xl text-purple-300 mb-3">No songs requested yet</p>
                              <p className="text-xl text-purple-400">Be the first to request a song!</p>
                            </div>
                          )
                        ) : (
                          <div className="text-center py-12">
                            <p className="text-xl text-purple-300">Request queue is hidden</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Offline State */}
                  <Card className="glass-effect border-purple-600 bg-purple-900/20">
                    <CardHeader>
                      <CardTitle className="text-3xl text-purple-100 flex items-center">
                        <Mic className="mr-3 text-purple-400" />
                        Currently Offline
                      </CardTitle>
                      <CardDescription className="text-purple-300 text-xl">
                        {profile.artist_name} is not currently hosting a karaoke session
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-12">
                        <Mic className="w-20 h-20 text-purple-400 mx-auto mb-6 opacity-50" />
                        <p className="text-2xl text-purple-300 mb-3">Check back later for live sessions!</p>
                        <p className="text-xl text-purple-400">
                          When {profile.artist_name} goes live, you'll be able to request songs and join the fun!
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>

            {/* Sidebar - QR Code and Info — above theme effects so nothing covers the code */}
            <div className="relative z-20 lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6"
              >
                {/* QR Code Card - Ensure it doesn't overlap */}
                {settings.showQRCode && (
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="sticky top-4"
                  >
                    <Card 
                      className="glass-effect border-purple-600"
                      style={{
                        backgroundColor: `rgba(0, 0, 0, ${settings.cardTransparency})`,
                        backdropFilter: settings.backdropBlur ? 'blur(10px)' : 'none'
                      }}
                    >
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg sm:text-xl text-purple-100 flex items-center">
                          <motion.div
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          >
                            <QrCode className="mr-2 w-5 h-5 sm:w-6 sm:h-6" />
                          </motion.div>
                          <span className="truncate">Request Songs</span>
                        </CardTitle>
                        <CardDescription className="text-purple-300 text-sm sm:text-lg">
                          Scan this QR code to request songs
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="text-center pt-2">
                        <motion.div 
                          className="bg-white p-4 sm:p-6 rounded-lg inline-block mb-4 sm:mb-6"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="flex justify-center">
                            <QRCode
                              value={publicUrl || ''}
                              size={200}
                              level="H"
                              includeMargin={true}
                              renderAs="svg"
                              style={{ maxWidth: '100%', height: 'auto' }}
                            />
                          </div>
                        </motion.div>
                        <p className="text-purple-300 text-sm sm:text-base mb-4 px-2">
                          Point your phone camera at this QR code to open the song request page
                        </p>
                        
                        {/* Brought to you by */}
                        <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-purple-600/30">
                          <div className="text-center">
                            <div className="text-purple-300 text-sm sm:text-lg font-medium">
                              Brought to you by:
                            </div>
                            <div className="text-purple-200 text-base sm:text-xl font-bold mt-1">
                              YourSongRequest.com
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Venue Info */}
                {settings.showVenueInfo && profile.bio && (
                  <Card 
                    className="glass-effect border-purple-600"
                    style={{
                      backgroundColor: `rgba(0, 0, 0, ${settings.cardTransparency})`,
                      backdropFilter: settings.backdropBlur ? 'blur(10px)' : 'none'
                    }}
                  >
                    <CardHeader>
                      <CardTitle className="text-xl text-purple-100">About</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-purple-300 text-lg">{profile.bio}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Social Links */}
                {settings.showSocialLinks && (profile.social_facebook || profile.social_x || profile.social_instagram || profile.social_tiktok) && (
                  <Card 
                    className="glass-effect border-purple-600"
                    style={{
                      backgroundColor: `rgba(0, 0, 0, ${settings.cardTransparency})`,
                      backdropFilter: settings.backdropBlur ? 'blur(10px)' : 'none'
                    }}
                  >
                    <CardHeader>
                      <CardTitle className="text-xl text-purple-100">Follow</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {profile.social_facebook && (
                          <a
                            href={`https://facebook.com/${profile.social_facebook}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-3 text-purple-300 hover:text-purple-200 transition-colors text-lg"
                          >
                            <span>📘</span>
                            <span>Facebook</span>
                          </a>
                        )}
                        {profile.social_x && (
                          <a
                            href={`https://x.com/${profile.social_x}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-3 text-purple-300 hover:text-purple-200 transition-colors text-lg"
                          >
                            <span>🐦</span>
                            <span>X (Twitter)</span>
                          </a>
                        )}
                        {profile.social_instagram && (
                          <a
                            href={`https://instagram.com/${profile.social_instagram}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-3 text-purple-300 hover:text-purple-200 transition-colors text-lg"
                          >
                            <span>📷</span>
                            <span>Instagram</span>
                          </a>
                        )}
                        {profile.social_tiktok && (
                          <a
                            href={`https://tiktok.com/@${profile.social_tiktok}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-3 text-purple-300 hover:text-purple-200 transition-colors text-lg"
                          >
                            <span>🎵</span>
                            <span>TikTok</span>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </div>
          </div>
        </main>
        
        {/* TV Display Ticker */}
        {(() => {
          console.log('📺 TV Display: Ticker display check:', {
            enabled: tickerSettings.enabled,
            text: tickerSettings.text,
            shouldShow: tickerSettings.enabled && tickerSettings.text
          });
          return tickerSettings.enabled && tickerSettings.text;
        })() && (
          <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm border-t border-purple-600/30 z-40 h-16">
            <div className="ticker-container overflow-hidden h-full flex items-center">
              <div 
                className="ticker-text text-purple-200 text-xl font-medium whitespace-nowrap"
                style={{
                  animation: `ticker ${Math.max(10, tickerSettings.text.length * 0.5)}s linear infinite`
                }}
              >
                {tickerSettings.text} • • • {tickerSettings.text} • • • {tickerSettings.text}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default KaraokeTVDisplayPage;
